/**
 * Swatch Hover Image Swap
 *
 * Handles hovering over color swatches on product cards to preview
 * the variant's image. Reverts to the default (first swatch) image
 * when the mouse leaves the swatch.
 */

class SwatchHoverImage {
  constructor() {
    this.injectStyles();
    this.init();
  }

  /**
   * Inject CSS to override the default secondary image hover behavior
   * for cards with color swatches
   */
  injectStyles() {
    const style = document.createElement('style');
    style.textContent = `
      /* ==============================================
       * Disable secondary image hover for cards with color swatches
       * The swatch system handles image swapping instead
       * ============================================== */

      /* Keep primary image visible on hover for cards with swatches */
      .card-wrapper.has-color-swatches:hover .media.media--hover-effect > img:first-child:not(:only-child) {
        opacity: 1 !important;
      }

      /* Hide secondary image on hover for cards with swatches */
      .card-wrapper.has-color-swatches:hover .media.media--hover-effect > img + img {
        opacity: 0 !important;
      }

      /* Collection highlight cards with swatches */
      .collection-highlight__card.has-color-swatches:hover .collection-highlight__card-image--primary {
        opacity: 1 !important;
      }

      .collection-highlight__card.has-color-swatches:hover .collection-highlight__card-image--secondary {
        opacity: 0 !important;
      }

      /* Smooth transition for swatch image swap */
      [data-product-card-image] {
        transition: opacity 0.2s ease-in-out;
      }

      /* ==============================================
       * FIX: Ensure swatches are clickable above card heading overlay
       * The .card__heading a::after has z-index: 1 and covers the entire card
       * ============================================== */

      /* Disable pointer events on the card heading overlay for cards with swatches */
      .card-wrapper.has-color-swatches .card__heading a::after {
        pointer-events: none;
      }

      /* Ensure swatch container is above the overlay */
      .card-wrapper.has-color-swatches .card-product-swatches {
        position: relative;
        z-index: 2;
      }

      /* Ensure individual swatches can receive hover events */
      .card-wrapper.has-color-swatches [data-swatch-hover] {
        position: relative;
        z-index: 2;
      }
    `;
    document.head.appendChild(style);
  }

  init() {
    // Use event delegation for better performance and to handle dynamically added content
    document.addEventListener('mouseenter', this.handleSwatchHover.bind(this), true);
    document.addEventListener('mouseleave', this.handleSwatchLeave.bind(this), true);

    // Also handle touch devices - show image on touch, revert when touching elsewhere
    document.addEventListener('touchstart', this.handleTouchStart.bind(this), true);
  }

  /**
   * Find the parent product card from a swatch element
   */
  getProductCard(element) {
    return element.closest('[data-product-card]');
  }

  /**
   * Find the product card image element
   */
  getCardImage(card) {
    return card.querySelector('[data-product-card-image]');
  }

  /**
   * Handle mouseenter on swatch
   */
  handleSwatchHover(event) {
    // Guard against non-Element targets (e.g., text nodes on mobile)
    if (!event.target || typeof event.target.closest !== 'function') return;

    const swatch = event.target.closest('[data-swatch-hover]');
    if (!swatch) return;

    const card = this.getProductCard(swatch);
    if (!card) return;

    const image = this.getCardImage(card);
    if (!image) return;

    // Get the variant image data from the swatch
    const variantSrc = swatch.dataset.variantImage;
    const variantSrcset = swatch.dataset.variantImageSrcset;
    const variantAlt = swatch.dataset.variantImageAlt;

    if (variantSrc) {
      this.swapImage(image, variantSrc, variantSrcset, variantAlt);

      // Mark the swatch as active for styling
      this.updateActiveSwatchState(card, swatch);
    }
  }

  /**
   * Handle mouseleave on swatch
   */
  handleSwatchLeave(event) {
    // Guard against non-Element targets (e.g., text nodes on mobile)
    if (!event.target || typeof event.target.closest !== 'function') return;

    const swatch = event.target.closest('[data-swatch-hover]');
    if (!swatch) return;

    // Check if we're moving to another swatch in the same card
    const relatedTarget = event.relatedTarget;
    if (relatedTarget && typeof relatedTarget.closest === 'function' && relatedTarget.closest('[data-swatch-hover]')) {
      const swatchCard = this.getProductCard(swatch);
      const relatedCard = this.getProductCard(relatedTarget);
      if (swatchCard === relatedCard) {
        // Moving to another swatch in the same card, don't revert yet
        return;
      }
    }

    const card = this.getProductCard(swatch);
    if (!card) return;

    const image = this.getCardImage(card);
    if (!image) return;

    // Revert to default image
    this.revertImage(image);

    // Restore the original active swatch state
    this.restoreActiveSwatchState(card);
  }

  /**
   * Handle touch events for mobile devices
   */
  handleTouchStart(event) {
    // Guard against non-Element targets (e.g., text nodes on mobile)
    if (!event.target || typeof event.target.closest !== 'function') return;

    const swatch = event.target.closest('[data-swatch-hover]');

    if (swatch) {
      // Touching a swatch - show its image
      const card = this.getProductCard(swatch);
      if (!card) return;

      const image = this.getCardImage(card);
      if (!image) return;

      const variantSrc = swatch.dataset.variantImage;
      const variantSrcset = swatch.dataset.variantImageSrcset;
      const variantAlt = swatch.dataset.variantImageAlt;

      if (variantSrc) {
        this.swapImage(image, variantSrc, variantSrcset, variantAlt);
        this.updateActiveSwatchState(card, swatch);
      }
    } else {
      // Touching elsewhere - revert all cards to default
      const activeCards = document.querySelectorAll('[data-product-card].swatch-preview-active');
      activeCards.forEach(card => {
        const image = this.getCardImage(card);
        if (image) {
          this.revertImage(image);
          this.restoreActiveSwatchState(card);
        }
      });
    }
  }

  /**
   * Swap the image src and srcset
   */
  swapImage(image, src, srcset, alt) {
    // Add a class for potential CSS transitions
    image.classList.add('swatch-preview');

    // Update the image attributes
    if (src) image.src = src;
    if (srcset) image.srcset = srcset;
    if (alt) image.alt = alt;

    // Mark the card as having an active swatch preview
    const card = this.getProductCard(image);
    if (card) {
      card.classList.add('swatch-preview-active');
    }
  }

  /**
   * Revert the image to its default state
   */
  revertImage(image) {
    const defaultSrc = image.dataset.defaultSrc;
    const defaultSrcset = image.dataset.defaultSrcset;
    const defaultAlt = image.dataset.defaultAlt;

    if (defaultSrc) image.src = defaultSrc;
    if (defaultSrcset) image.srcset = defaultSrcset;
    if (defaultAlt) image.alt = defaultAlt;

    image.classList.remove('swatch-preview');

    // Remove the active preview class from the card
    const card = this.getProductCard(image);
    if (card) {
      card.classList.remove('swatch-preview-active');
    }
  }

  /**
   * Update which swatch appears active during hover
   */
  updateActiveSwatchState(card, hoveredSwatch) {
    // Store the original active swatch if not already stored
    if (!card.dataset.originalActiveSwatch) {
      const originalActive = card.querySelector('[data-swatch-hover].active');
      if (originalActive) {
        card.dataset.originalActiveSwatch = 'stored';
        originalActive.classList.add('original-active');
        originalActive.classList.remove('active');
      }
    }

    // Remove active class from all swatches in this card
    const allSwatches = card.querySelectorAll('[data-swatch-hover]');
    allSwatches.forEach(s => s.classList.remove('active', 'hover-active'));

    // Add active class to the hovered swatch
    hoveredSwatch.classList.add('active', 'hover-active');
  }

  /**
   * Restore the original active swatch state
   */
  restoreActiveSwatchState(card) {
    // Remove hover-active from all swatches
    const allSwatches = card.querySelectorAll('[data-swatch-hover]');
    allSwatches.forEach(s => s.classList.remove('active', 'hover-active'));

    // Restore the original active swatch
    const originalActive = card.querySelector('[data-swatch-hover].original-active');
    if (originalActive) {
      originalActive.classList.add('active');
      originalActive.classList.remove('original-active');
      delete card.dataset.originalActiveSwatch;
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new SwatchHoverImage());
} else {
  new SwatchHoverImage();
}
